import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../lib/config';

const API = API_BASE;

const TimesheetsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [previews, setPreviews] = useState({}); // timesheet_id -> preview data
  const [adjustments, setAdjustments] = useState({}); // timesheet_id -> adjustment list
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ job_id: '', status: '' });

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (filter.job_id) qs.set('job_id', filter.job_id);
      if (filter.status) qs.set('status', filter.status);
      const resp = await fetch(`${API}/employer/timesheets?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('list failed');
      const data = await resp.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  const rebuild = async () => {
    try {
      const resp = await fetch(`${API}/employer/timesheets/rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ job_id: filter.job_id || null })
      });
      if (!resp.ok) throw new Error('rebuild failed');
      await fetchList();
      alert('再集計が完了しました');
    } catch (e) {
      console.error(e);
      alert('再集計に失敗しました');
    }
  };

  const markNoShow = async (tsId) => {
    try {
      const reason = prompt('無断欠勤の理由・メモ（任意）を入力してください') || '';
      const url = new URL(`${API}/employer/timesheets/${tsId}/no-show`);
      if (reason) url.searchParams.set('reason', reason);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('no-show failed');
      await fetchList();
      alert('無断欠勤として処理しました');
    } catch (e) {
      console.error(e);
      alert('無断欠勤の処理に失敗しました');
    }
  };

  const fetchAdjustments = async (tsId) => {
    try {
      const resp = await fetch(`${API}/employer/timesheets/${encodeURIComponent(tsId)}/adjustments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('adjustments failed');
      const data = await resp.json();
      setAdjustments((p) => ({ ...p, [tsId]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      console.error(e);
      setAdjustments((p) => ({ ...p, [tsId]: [] }));
    }
  };

  const approveAdj = async (tsId, adjId) => {
    try {
      const resp = await fetch(`${API}/employer/timesheets/${encodeURIComponent(tsId)}/adjustments/${encodeURIComponent(adjId)}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('approve adjustment failed');
      await fetchAdjustments(tsId);
      await fetchPreview(tsId);
      alert('差異申請を承認しました');
    } catch (e) {
      console.error(e);
      alert('承認に失敗しました');
    }
  };

  const rejectAdj = async (tsId, adjId) => {
    try {
      const reason = prompt('却下理由を入力してください（任意）') || '';
      const url = new URL(`${API}/employer/timesheets/${tsId}/adjustments/${adjId}/reject`);
      if (reason) url.searchParams.set('reason', reason);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('reject adjustment failed');
      await fetchAdjustments(tsId);
      alert('差異申請を却下しました');
    } catch (e) {
      console.error(e);
      alert('却下に失敗しました');
    }
  };

  const approveAndPay = async (tsId) => {
    try {
      const resp = await fetch(`${API}/employer/timesheets/${tsId}/approve-and-pay`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('approve-and-pay failed');
      const data = await resp.json();
      await fetchList();
      if (previews[tsId]) {
        await fetchPreview(tsId);
      }
      alert(`承認と送金を実行しました（¥${data.amount}${data.transfer_id ? ` / ${data.transfer_id}` : ''}）`);
    } catch (e) {
      console.error(e);
      alert('承認と送金に失敗しました（のちほど再実行してください）');
    }
  };

  const exportCsv = () => {
    try {
      const header = ['work_date', 'user_id', 'job_id', 'total_hours', 'status'];
      const rows = items.map(ts => [ts.work_date, ts.user_id, ts.job_id, Number(ts.total_hours || 0).toFixed(2), ts.status]);
      const csv = [header, ...rows].map(r => r.map(v => `"${(v ?? '').toString().replaceAll('"', '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheets_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('CSV出力に失敗しました');
    }
  };

  const fetchPreview = async (tsId) => {
    try {
      const resp = await fetch(`${API}/employer/pay/preview?timesheet_id=${encodeURIComponent(tsId)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('preview failed');
      const data = await resp.json();
      setPreviews((p) => ({ ...p, [tsId]: data }));
    } catch (e) {
      console.error(e);
      alert('見積の取得に失敗しました');
    }
  };

  const act = async (tsId, action) => {
    try {
      const resp = await fetch(`${API}/employer/timesheets/${tsId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error(`${action} failed`);
      await fetchList();
    } catch (e) {
      console.error(e);
      alert('操作に失敗しました');
    }
  };

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">タイムシート承認</h1>
        <div className="flex gap-2">
          <input
            className="px-3 py-2 border rounded-md"
            placeholder="求人IDで絞り込み"
            value={filter.job_id}
            onChange={(e) => setFilter(prev => ({ ...prev, job_id: e.target.value }))}
          />
          <select
            className="px-3 py-2 border rounded-md"
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">すべて</option>
            <option value="pending">未承認</option>
            <option value="approved">承認済み</option>
            <option value="rejected">差戻し</option>
            <option value="locked">ロック</option>
          </select>
          <button
            onClick={fetchList}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            更新
          </button>
          <button
            onClick={rebuild}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
          >
            再集計
          </button>
          <button
            onClick={exportCsv}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
          >
            CSVダウンロード
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">日付</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">求職者</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">求人ID</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">時間(hrs)</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">状態</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">読み込み中...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">データがありません</td>
                </tr>
              ) : (
                items.map((ts) => (
                  <>
                    <tr key={(ts.id || `${ts.user_id}-${ts.job_id}-${ts.work_date}`) + '-row'} className="border-t">
                      <td className="px-4 py-3 text-sm">{ts.work_date}</td>
                      <td className="px-4 py-3 text-sm">{ts.user_id}</td>
                      <td className="px-4 py-3 text-sm">{ts.job_id}</td>
                      <td className="px-4 py-3 text-sm text-right">{Number(ts.total_hours || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-white text-xs ${ts.status === 'approved' ? 'bg-green-600' : ts.status === 'rejected' ? 'bg-red-600' : ts.status === 'locked' ? 'bg-gray-600' : 'bg-yellow-600'
                          }`}>
                          {ts.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => fetchPreview(ts.id)} className="px-3 py-1 bg-blue-600 text-white rounded">見積</button>
                          <button onClick={() => approveAndPay(ts.id)} className="px-3 py-1 bg-indigo-600 text-white rounded">承認+送金</button>
                          <button onClick={() => act(ts.id, 'approve')} className="px-3 py-1 bg-green-600 text-white rounded">承認</button>
                          <button onClick={() => act(ts.id, 'reject')} className="px-3 py-1 bg-red-600 text-white rounded">差戻し</button>
                          <button onClick={() => act(ts.id, 'lock')} className="px-3 py-1 bg-gray-700 text-white rounded">ロック</button>
                        </div>
                      </td>
                    </tr>
                    {previews[ts.id] && (
                      <tr key={(ts.id || `${ts.user_id}-${ts.job_id}-${ts.work_date}`) + '-preview'} className="bg-gray-50">
                        <td colSpan={6} className="px-4 py-3 text-sm">
                          <div className="flex flex-wrap gap-4 items-center">
                            <div>基準時給: <span className="font-semibold">¥{previews[ts.id].base_rate}</span></div>
                            <div>通常: <span className="font-semibold">{previews[ts.id].base_hours.toFixed(2)}h</span></div>
                            <div>残業: <span className="font-semibold">{previews[ts.id].overtime_hours.toFixed(2)}h</span></div>
                            <div>深夜: <span className="font-semibold">{previews[ts.id].night_hours.toFixed(2)}h</span></div>
                            <div>小計: <span className="font-semibold">¥{previews[ts.id].base_pay}</span></div>
                            <div>残業加算: <span className="font-semibold">¥{previews[ts.id].overtime_bonus}</span></div>
                            <div>深夜加算: <span className="font-semibold">¥{previews[ts.id].night_bonus}</span></div>
                            <div>交通費: <span className="font-semibold">¥{previews[ts.id].transport}</span></div>
                            <div>合計: <span className="font-bold text-blue-700">¥{previews[ts.id].total}</span></div>
                            <div className="text-xs text-gray-500">丸め: 15分単位</div>
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button onClick={() => markNoShow(ts.id)} className="px-2 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded">無断欠勤として処理</button>
                            <button onClick={() => navigate(`/reviews/new?target_id=${encodeURIComponent(ts.user_id)}&target_role=job_seeker`)} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded">レビューを書く</button>
                          </div>
                          <div className="mt-3">
                            <button onClick={() => fetchAdjustments(ts.id)} className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded">差異申請の表示/更新</button>
                          </div>
                          {Array.isArray(adjustments[ts.id]) && adjustments[ts.id].length > 0 && (
                            <div className="mt-2 border-t pt-2">
                              <div className="text-xs text-gray-600 mb-1">差異申請</div>
                              <ul className="space-y-2">
                                {adjustments[ts.id].map(adj => (
                                  <li key={adj.id} className="flex items-center justify-between text-xs">
                                    <div className="flex-1">
                                      <span className="font-semibold mr-2">{adj.status}</span>
                                      <span>base:{adj.fields?.base_hours ?? '-'} / ot:{adj.fields?.overtime_hours ?? '-'} / night:{adj.fields?.night_hours ?? '-'} / trans:{adj.fields?.transport ?? '-'}</span>
                                      {adj.note ? <span className="ml-2 text-gray-500">({adj.note})</span> : null}
                                    </div>
                                    <div className="flex gap-1">
                                      {adj.status === 'pending' && (
                                        <>
                                          <button onClick={() => approveAdj(ts.id, adj.id)} className="px-2 py-1 bg-green-600 text-white rounded">承認</button>
                                          <button onClick={() => rejectAdj(ts.id, adj.id)} className="px-2 py-1 bg-red-600 text-white rounded">却下</button>
                                        </>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TimesheetsPage;
